// Centralized task definitions for all sections

export interface Task {
  id: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  type: 'fill-in-the-blanks' | 'single-select' | 'inline-select' | 'reading-comprehension' | 'matching' | 'ordering' | 'multi-select' | 'drag-and-drop';
  section: 'grammar' | 'vocabulary' | 'reading' | 'writing';
  subcategory?: string;
  level: number; // 1, 2, 3 for difficulty progression
}

export const ALL_TASKS: Task[] = [
  // Grammar Tasks - Sorted by difficulty (Beginner -> Intermediate -> Advanced), then by level
  // Beginner Level 1
  {
    id: 'present-simple-1',
    title: 'Present Simple',
    description: 'Fill in the blanks',
    difficulty: 'Beginner',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 1
  },
  {
    id: 'present-continuous-1',
    title: 'Present Continuous',
    description: 'Fill in the blanks',
    difficulty: 'Beginner',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 1
  },
  {
    id: 'past-simple-1',
    title: 'Past Simple',
    description: 'Matching',
    difficulty: 'Beginner',
    type: 'matching',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 1
  },
  {
    id: 'past-continuous-1',
    title: 'Past Continuous',
    description: 'Matching',
    difficulty: 'Beginner',
    type: 'matching',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 1
  },
  {
    id: 'future-simple-2',
    title: 'Future Simple',
    description: 'Fill in the blanks',
    difficulty: 'Beginner',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 1
  },
  {
    id: 'sentence-structure-1',
    title: 'Sentence Structure Basics',
    description: 'Fill in the blanks',
    difficulty: 'Beginner',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Sentence Structure',
    level: 1
  },
  {
    id: 'verb-patterns-1',
    title: 'Verb Patterns Basics',
    description: 'Fill in the blanks',
    difficulty: 'Beginner',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Verb Patterns',
    level: 1
  },
  {
    id: 'voice-clauses-1',
    title: 'Voice and Clauses Basics',
    description: 'Matching',
    difficulty: 'Beginner',
    type: 'matching',
    section: 'grammar',
    subcategory: 'Voice & Clauses',
    level: 1
  },
  {
    id: 'modal-verbs-1',
    title: 'Modal Verbs (can, could, should)',
    description: 'Multi-select',
    difficulty: 'Beginner',
    type: 'multi-select',
    section: 'grammar',
    subcategory: 'Modals & Preferences',
    level: 1
  },
  {
    id: 'conditionals-basic-1',
    title: 'Conditionals Basics',
    description: 'Fill in the blanks',
    difficulty: 'Beginner',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Conditionals',
    level: 1
  },
  {
    id: 'prepositions-1',
    title: 'Prepositions of Time',
    description: 'Matching',
    difficulty: 'Beginner',
    type: 'matching',
    section: 'grammar',
    subcategory: 'Prepositions',
    level: 1
  },
  {
    id: 'prepositions-2',
    title: 'Prepositions of Place',
    description: 'Matching',
    difficulty: 'Beginner',
    type: 'matching',
    section: 'grammar',
    subcategory: 'Prepositions',
    level: 1
  },
  {
    id: 'comparatives-1',
    title: 'Comparatives',
    description: 'Drag and drop',
    difficulty: 'Beginner',
    type: 'drag-and-drop',
    section: 'grammar',
    subcategory: 'Comparisons',
    level: 1
  },
  {
    id: 'superlatives-1',
    title: 'Superlatives',
    description: 'Fill in the blanks',
    difficulty: 'Beginner',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Comparisons',
    level: 1
  },
  {
    id: 'comparisons-2',
    title: 'Comparisons in Context',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Comparisons',
    level: 2
  },
  {
    id: 'countable-uncountable-1',
    title: 'Countable and Uncountable Nouns',
    description: 'Multi-select',
    difficulty: 'Beginner',
    type: 'multi-select',
    section: 'grammar',
    subcategory: 'Nouns & Articles',
    level: 1
  },
  {
    id: 'pronouns-1',
    title: 'Subject and Object Pronouns',
    description: 'Fill in the blanks',
    difficulty: 'Beginner',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Pronouns & Possessives',
    level: 1
  },
  {
    id: 'possessive-1',
    title: 'Possessive Adjectives and Pronouns',
    description: 'Fill in the blanks',
    difficulty: 'Beginner',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Pronouns & Possessives',
    level: 1
  },
  // Intermediate Level 2
  {
    id: 'drag-drop-tenses',
    title: 'Sort by Tense',
    description: 'Drag and drop',
    difficulty: 'Beginner',
    type: 'drag-and-drop',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 1
  },
  {
    id: 'articles-2',
    title: 'Articles (a, an, the)',
    description: 'Inline select',
    difficulty: 'Intermediate',
    type: 'inline-select',
    section: 'grammar',
    subcategory: 'Nouns & Articles',
    level: 2
  },
  {
    id: 'prepositions-advanced-2',
    title: 'Prepositions in Context',
    description: 'Drag and drop',
    difficulty: 'Intermediate',
    type: 'drag-and-drop',
    section: 'grammar',
    subcategory: 'Prepositions',
    level: 2
  },
  {
    id: 'present-perfect-2',
    title: 'Present Perfect',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 2
  },
  {
    id: 'past-perfect-2',
    title: 'Past Perfect',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 2
  },
  {
    id: 'future-continuous-2',
    title: 'Future Continuous',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 2
  },
  {
    id: 'modal-verbs-2',
    title: 'Modal Verbs (must, might, may)',
    description: 'Multi-select',
    difficulty: 'Intermediate',
    type: 'multi-select',
    section: 'grammar',
    subcategory: 'Modals & Preferences',
    level: 2
  },
  {
    id: 'conditionals-1',
    title: 'First Conditional',
    description: 'Drag and drop',
    difficulty: 'Intermediate',
    type: 'drag-and-drop',
    section: 'grammar',
    subcategory: 'Conditionals',
    level: 2
  },
  {
    id: 'conditionals-2',
    title: 'Second Conditional',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Conditionals',
    level: 2
  },
  {
    id: 'passive-voice-2',
    title: 'Passive Voice',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Voice & Clauses',
    level: 2
  },
  {
    id: 'relative-clauses-2',
    title: 'Relative Clauses',
    description: 'Multi-select',
    difficulty: 'Intermediate',
    type: 'multi-select',
    section: 'grammar',
    subcategory: 'Voice & Clauses',
    level: 2
  },
  {
    id: 'reported-speech-2',
    title: 'Reported Speech',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Voice & Clauses',
    level: 2
  },
  {
    id: 'gerunds-infinitives-2',
    title: 'Gerunds and Infinitives',
    description: 'Drag and drop',
    difficulty: 'Intermediate',
    type: 'drag-and-drop',
    section: 'grammar',
    subcategory: 'Verb Patterns',
    level: 2
  },
  {
    id: 'question-tags-2',
    title: 'Question Tags',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Sentence Structure',
    level: 2
  },
  {
    id: 'pronouns-2',
    title: 'Pronouns in Context',
    description: 'Multi-select',
    difficulty: 'Intermediate',
    type: 'multi-select',
    section: 'grammar',
    subcategory: 'Pronouns & Possessives',
    level: 2
  },
  {
    id: 'phrasal-verbs-2',
    title: 'Phrasal Verbs',
    description: 'Drag and drop',
    difficulty: 'Intermediate',
    type: 'drag-and-drop',
    section: 'grammar',
    subcategory: 'Verb Patterns',
    level: 2
  },
  {
    id: 'used-to-2',
    title: 'Used To',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Modals & Preferences',
    level: 2
  },
  {
    id: 'would-rather-2',
    title: 'Would Rather and Prefer',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Modals & Preferences',
    level: 2
  },
  // Advanced Level 3
  {
    id: 'perfect-tenses-3',
    title: 'Perfect Tenses',
    description: 'Fill in the blanks',
    difficulty: 'Advanced',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 3
  },
  {
    id: 'conditionals-3',
    title: 'Third Conditional',
    description: 'Fill in the blanks',
    difficulty: 'Advanced',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Conditionals',
    level: 3
  },
  {
    id: 'wish-if-only-3',
    title: 'Wish and If Only',
    description: 'Fill in the blanks',
    difficulty: 'Advanced',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Conditionals',
    level: 3
  },
  {
    id: 'mixed-tenses-3',
    title: 'Mixed Tenses',
    description: 'Fill in the blanks',
    difficulty: 'Advanced',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Tenses',
    level: 3
  },
  {
    id: 'prepositions-3',
    title: 'Advanced Prepositions',
    description: 'Fill in the blanks',
    difficulty: 'Advanced',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Prepositions',
    level: 3
  },
  {
    id: 'comparisons-3',
    title: 'Advanced Comparisons',
    description: 'Fill in the blanks',
    difficulty: 'Advanced',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Comparisons',
    level: 3
  },
  {
    id: 'pronouns-3',
    title: 'Advanced Pronoun Precision',
    description: 'Multi-select',
    difficulty: 'Advanced',
    type: 'multi-select',
    section: 'grammar',
    subcategory: 'Pronouns & Possessives',
    level: 3
  },
  {
    id: 'articles-3',
    title: 'Advanced Articles',
    description: 'Inline select',
    difficulty: 'Advanced',
    type: 'inline-select',
    section: 'grammar',
    subcategory: 'Nouns & Articles',
    level: 3
  },
  {
    id: 'modal-verbs-3',
    title: 'Advanced Modal Verbs',
    description: 'Multi-select',
    difficulty: 'Advanced',
    type: 'multi-select',
    section: 'grammar',
    subcategory: 'Modals & Preferences',
    level: 3
  },
  {
    id: 'sentence-structure-3',
    title: 'Advanced Sentence Structure',
    description: 'Fill in the blanks',
    difficulty: 'Advanced',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Sentence Structure',
    level: 3
  },
  {
    id: 'verb-patterns-3',
    title: 'Advanced Verb Patterns',
    description: 'Fill in the blanks',
    difficulty: 'Advanced',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Verb Patterns',
    level: 3
  },
  {
    id: 'voice-clauses-3',
    title: 'Advanced Voice and Clauses',
    description: 'Fill in the blanks',
    difficulty: 'Advanced',
    type: 'fill-in-the-blanks',
    section: 'grammar',
    subcategory: 'Voice & Clauses',
    level: 3
  },

  // Vocabulary Tasks
  {
    id: 'vocabulary-basic',
    title: 'Basic Vocabulary',
    description: 'Multiple choice',
    difficulty: 'Beginner',
    type: 'single-select',
    section: 'vocabulary',
    subcategory: 'Core Vocabulary',
    level: 1
  },
  {
    id: 'vocabulary-core-intermediate',
    title: 'Core Vocabulary in Context',
    description: 'Multiple choice',
    difficulty: 'Intermediate',
    type: 'single-select',
    section: 'vocabulary',
    subcategory: 'Core Vocabulary',
    level: 2
  },
  {
    id: 'vocabulary-core-advanced',
    title: 'Core Vocabulary Precision',
    description: 'Multiple choice',
    difficulty: 'Advanced',
    type: 'single-select',
    section: 'vocabulary',
    subcategory: 'Core Vocabulary',
    level: 3
  },
  {
    id: 'vocabulary-nature',
    title: 'Nature & Environment',
    description: 'Drag and drop',
    difficulty: 'Beginner',
    type: 'drag-and-drop',
    section: 'vocabulary',
    subcategory: 'Themes',
    level: 1
  },
  {
    id: 'vocabulary-food',
    title: 'Food & Cooking',
    description: 'Drag and drop',
    difficulty: 'Beginner',
    type: 'drag-and-drop',
    section: 'vocabulary',
    subcategory: 'Themes',
    level: 1
  },
  {
    id: 'vocabulary-intermediate',
    title: 'Intermediate Vocabulary',
    description: 'Multiple choice',
    difficulty: 'Intermediate',
    type: 'single-select',
    section: 'vocabulary',
    subcategory: 'Word Skills',
    level: 2
  },
  {
    id: 'vocabulary-word-skills-basic',
    title: 'Word Skills Fundamentals',
    description: 'Matching',
    difficulty: 'Beginner',
    type: 'matching',
    section: 'vocabulary',
    subcategory: 'Word Skills',
    level: 1
  },
  {
    id: 'vocabulary-technology',
    title: 'Technology & Internet',
    description: 'Multiple choice',
    difficulty: 'Intermediate',
    type: 'single-select',
    section: 'vocabulary',
    subcategory: 'Themes',
    level: 2
  },
  {
    id: 'vocabulary-emotions',
    title: 'Emotions & Feelings',
    description: 'Multi-select',
    difficulty: 'Intermediate',
    type: 'multi-select',
    section: 'vocabulary',
    subcategory: 'Themes',
    level: 2
  },
  {
    id: 'vocabulary-themes-advanced',
    title: 'Themes: Advanced Usage',
    description: 'Multiple choice',
    difficulty: 'Advanced',
    type: 'single-select',
    section: 'vocabulary',
    subcategory: 'Themes',
    level: 3
  },
  {
    id: 'vocabulary-advanced',
    title: 'Advanced Vocabulary',
    description: 'Multiple choice',
    difficulty: 'Advanced',
    type: 'single-select',
    section: 'vocabulary',
    subcategory: 'Word Skills',
    level: 3
  },
  {
    id: 'drag-drop-word-categories',
    title: 'Sort Words by Category',
    description: 'Drag and drop',
    difficulty: 'Intermediate',
    type: 'drag-and-drop',
    section: 'vocabulary',
    subcategory: 'Word Skills',
    level: 2
  },


  // Writing Tasks - Objective, non-AI tasks
  {
    id: 'mad-libs-adventure',
    title: 'Mad Libs Adventure',
    description: 'Fill in the blanks',
    difficulty: 'Beginner',
    type: 'fill-in-the-blanks',
    section: 'writing',
    subcategory: 'Creative Fill-in',
    level: 1
  },
  {
    id: 'creative-fill-in-2',
    title: 'Creative Fill-In Stories',
    description: 'Fill in the blanks',
    difficulty: 'Intermediate',
    type: 'fill-in-the-blanks',
    section: 'writing',
    subcategory: 'Creative Fill-in',
    level: 2
  },
  {
    id: 'creative-fill-in-3',
    title: 'Creative Fill-In Mastery',
    description: 'Fill in the blanks',
    difficulty: 'Advanced',
    type: 'fill-in-the-blanks',
    section: 'writing',
    subcategory: 'Creative Fill-in',
    level: 3
  },
  {
    id: 'sentence-revision-choice',
    title: 'Sentence Revision Choice',
    description: 'Multiple choice',
    difficulty: 'Beginner',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Sentence Skills',
    level: 1
  },
  {
    id: 'sentence-revision-advanced',
    title: 'Sentence Revision Studio',
    description: 'Multiple choice',
    difficulty: 'Advanced',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Sentence Skills',
    level: 3
  },
  {
    id: 'sentence-revision-intermediate',
    title: 'Sentence Revision Workshop',
    description: 'Multiple choice',
    difficulty: 'Intermediate',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Sentence Skills',
    level: 2
  },
  {
    id: 'punctuation-practice',
    title: 'Punctuation Practice',
    description: 'Multiple choice',
    difficulty: 'Beginner',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Punctuation',
    level: 1
  },
  {
    id: 'punctuation-intermediate',
    title: 'Punctuation in Context',
    description: 'Multiple choice',
    difficulty: 'Intermediate',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Punctuation',
    level: 2
  },
  {
    id: 'punctuation-advanced',
    title: 'Advanced Punctuation',
    description: 'Multiple choice',
    difficulty: 'Advanced',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Punctuation',
    level: 3
  },
  {
    id: 'transition-word-choice',
    title: 'Transition Word Choice',
    description: 'Multiple choice',
    difficulty: 'Intermediate',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Transitions',
    level: 2
  },
  {
    id: 'transition-basics-1',
    title: 'Transition Basics',
    description: 'Multiple choice',
    difficulty: 'Beginner',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Transitions',
    level: 1
  },
  {
    id: 'word-choice-clarity',
    title: 'Word Choice for Clarity',
    description: 'Multiple choice',
    difficulty: 'Intermediate',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Word Choice',
    level: 2
  },
  {
    id: 'word-choice-basics-1',
    title: 'Word Choice Basics',
    description: 'Multiple choice',
    difficulty: 'Beginner',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Word Choice',
    level: 1
  },
  {
    id: 'word-choice-advanced',
    title: 'Word Choice for Precision',
    description: 'Multiple choice',
    difficulty: 'Advanced',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Word Choice',
    level: 3
  },
  {
    id: 'transition-matching',
    title: 'Match Transitions',
    description: 'Matching',
    difficulty: 'Intermediate',
    type: 'matching',
    section: 'writing',
    subcategory: 'Transitions',
    level: 2
  },
  {
    id: 'transition-advanced-usage',
    title: 'Transition Logic: Advanced',
    description: 'Multiple choice',
    difficulty: 'Advanced',
    type: 'single-select',
    section: 'writing',
    subcategory: 'Transitions',
    level: 3
  },
  {
    id: 'sentence-ordering',
    title: 'Sentence Ordering',
    description: 'Ordering',
    difficulty: 'Beginner',
    type: 'ordering',
    section: 'writing',
    subcategory: 'Organization',
    level: 1
  },
  {
    id: 'paragraph-organization-2',
    title: 'Paragraph Organization',
    description: 'Ordering',
    difficulty: 'Intermediate',
    type: 'ordering',
    section: 'writing',
    subcategory: 'Organization',
    level: 2
  },
  {
    id: 'paragraph-organization-3',
    title: 'Argument Organization Studio',
    description: 'Ordering',
    difficulty: 'Advanced',
    type: 'ordering',
    section: 'writing',
    subcategory: 'Organization',
    level: 3
  },
  {
    id: 'evidence-multi-select',
    title: 'Find the Evidence',
    description: 'Multi-select',
    difficulty: 'Intermediate',
    type: 'multi-select',
    section: 'writing',
    subcategory: 'Evidence',
    level: 2
  },
  {
    id: 'evidence-basics-1',
    title: 'Evidence Basics',
    description: 'Multi-select',
    difficulty: 'Beginner',
    type: 'multi-select',
    section: 'writing',
    subcategory: 'Evidence',
    level: 1
  },
  {
    id: 'evidence-extended-arguments',
    title: 'Evidence Builder: Extended Arguments',
    description: 'Multi-select',
    difficulty: 'Advanced',
    type: 'multi-select',
    section: 'writing',
    subcategory: 'Evidence',
    level: 3
  },
  {
    id: 'evidence-academic-analysis',
    title: 'Evidence Builder: Academic Analysis',
    description: 'Multi-select',
    difficulty: 'Advanced',
    type: 'multi-select',
    section: 'writing',
    subcategory: 'Evidence',
    level: 3
  },
  {
    id: 'drag-drop-structure',
    title: 'Organize Paragraph Parts',
    description: 'Drag and drop',
    difficulty: 'Beginner',
    type: 'drag-and-drop',
    section: 'writing',
    subcategory: 'Organization',
    level: 1
  },

  // Reading Tasks - Classic Literature
  {
    id: 'poe-telltale-comprehension',
    title: 'The Tell-Tale Heart - Comprehension',
    description: "Read this classic short story and test your understanding.",
    difficulty: 'Intermediate',
    type: 'reading-comprehension',
    section: 'reading',
    subcategory: 'American Classics',
    level: 2
  },
  {
    id: 'ohenry-gift-magi',
    title: 'The Gift of the Magi - Analysis',
    description: "Read this famous Christmas story about sacrifice and love.",
    difficulty: 'Beginner',
    type: 'reading-comprehension',
    section: 'reading',
    subcategory: 'American Classics',
    level: 1
  },
  {
    id: 'poe-telltale-analysis-3',
    title: 'The Tell-Tale Heart - Deep Analysis',
    description: "Read this psychological classic.",
    difficulty: 'Advanced',
    type: 'reading-comprehension',
    section: 'reading',
    subcategory: 'American Classics',
    level: 3
  },
  {
    id: 'dickens-christmas-carol-basic',
    title: 'A Christmas Carol - Essentials',
    description: "Read the opening passage.",
    difficulty: 'Beginner',
    type: 'reading-comprehension',
    section: 'reading',
    subcategory: 'British Classics',
    level: 1
  },
  {
    id: 'chekhov-lady-dog',
    title: 'The Lady with the Dog - Themes',
    description: "Read this literary masterpiece.",
    difficulty: 'Advanced',
    type: 'reading-comprehension',
    section: 'reading',
    subcategory: 'Russian Classics',
    level: 3
  },
  {
    id: 'chekhov-lady-dog-intermediate',
    title: 'The Lady with the Dog - Comprehension',
    description: 'Reading',
    difficulty: 'Intermediate',
    type: 'reading-comprehension',
    section: 'reading',
    subcategory: 'Russian Classics',
    level: 2
  },
  {
    id: 'chekhov-lady-dog-basic',
    title: 'The Lady with the Dog - Essentials',
    description: 'Reading',
    difficulty: 'Beginner',
    type: 'reading-comprehension',
    section: 'reading',
    subcategory: 'Russian Classics',
    level: 1
  },
  {
    id: 'dickens-christmas-carol',
    title: 'A Christmas Carol - Character Study',
    description: "Read this timeless holiday classic.",
    difficulty: 'Intermediate',
    type: 'reading-comprehension',
    section: 'reading',
    subcategory: 'British Classics',
    level: 2
  },
  {
    id: 'dickens-christmas-carol-advanced',
    title: 'A Christmas Carol - Rhetorical Analysis',
    description: 'Reading',
    difficulty: 'Advanced',
    type: 'reading-comprehension',
    section: 'reading',
    subcategory: 'British Classics',
    level: 3
  },
  {
    id: 'twain-tom-sawyer',
    title: 'Tom Sawyer - Adventure Analysis',
    description: "Read this classic adventure story.",
    difficulty: 'Beginner',
    type: 'reading-comprehension',
    section: 'reading',
    subcategory: 'American Classics',
    level: 1
  },

];

// Helper functions
export const getTasksBySection = (section: string): Task[] => {
  return ALL_TASKS.filter(task => task.section === section);
};

export const getTaskById = (id: string): Task | undefined => {
  return ALL_TASKS.find(task => task.id === id);
};

export const getTaskUrl = (task: Task): string => {
  return `/${task.section}/${task.id}`;
};

export const getSectionUrl = (section: string): string => {
  return `/${section}`;
};

export const getTaskSubcategory = (task: Task): string => {
  return task.subcategory || 'General';
};

export const groupTasksBySubcategory = (tasks: Task[]): { subcategory: string; tasks: Task[] }[] => {
  const groups = new Map<string, Task[]>();
  tasks.forEach(task => {
    const subcategory = getTaskSubcategory(task);
    if (!groups.has(subcategory)) {
      groups.set(subcategory, []);
    }
    groups.get(subcategory)!.push(task);
  });
  return Array.from(groups.entries()).map(([subcategory, groupTasks]) => ({
    subcategory,
    tasks: groupTasks
  }));
};
